import { useCallback, useEffect, useRef, useState } from "react";
import Nav from "@/components/Nav";
import api from "@/lib/api";
import { useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Send } from "lucide-react";

export default function Chat() {
  const { id } = useParams();
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [candidate, setCandidate] = useState(null);
  const endRef = useRef(null);

  const load = useCallback(async () => {
    const [{ data }, { data: snap }] = await Promise.all([
      api.get(`/chat/${id}`),
      api.get(`/matches/${id}/snapshot`).catch(() => ({ data: null })),
    ]);
    setMessages(data.messages);
    if (snap) setCandidate(snap);
  }, [id]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    const t = text; setText("");
    await api.post("/chat/send", { match_id: id, text: t });
    await load();
  };

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex flex-col">
      <Nav />
      <div className="max-w-3xl w-full mx-auto flex-1 flex flex-col px-6 py-8">
        <div className="mb-6">
          <div className="overline">Secure Conversation</div>
          <h1 className="font-serif-display text-3xl text-[#1E1B4B] mt-1">{candidate?.name || "Chat"}</h1>
          {candidate && <p className="text-sm text-stone-500">{candidate.overall_score}% aligned · {candidate.profile.city}</p>}
        </div>

        <div className="flex-1 border border-stone-200 rounded-2xl bg-white p-6 overflow-y-auto space-y-4 min-h-[400px]">
          {messages.length === 0 && <div className="text-stone-400 text-center pt-16">Say hello. Every great story starts with a first message.</div>}
          {messages.map((m) => {
            const mine = m.from_id === user.id;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] px-4 py-3 rounded-2xl ${mine ? "bg-[#1E1B4B] text-white" : "bg-stone-100 text-[#1E1B4B]"}`}>
                  <div className="text-xs opacity-70 mb-1">{m.from_name}</div>
                  <div className="text-sm">{m.text}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>

        <form onSubmit={send} className="mt-4 flex gap-2">
          <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Write thoughtfully…"
            className="flex-1 border border-stone-300 rounded-full px-5 py-3 bg-white focus:border-[#1E1B4B]" />
          <button data-testid="chat-send-btn" className="btn-pill text-white px-5" style={{ background: "#1E1B4B" }}><Send size={16} /></button>
        </form>
      </div>
    </div>
  );
}
